// src/Components/DeviceTrendChart.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api'; // ✅ default import
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler,
} from 'chart.js';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler
);

/**
 * 온도(단독) + 연기지표 2개(합쳐서) 기간 평균 라인 차트
 * - 시작일과 종료일이 같으면 → 시간대별(0~23시) 평균
 * - 다르면 → 일자별 평균
 *
 * API
 * 1) /api/devices/{id}/daily-avg?start=YYYY-MM-DD&end=YYYY-MM-DD
 * 2) /api/devices/{id}/hourly-avg?date=YYYY-MM-DD
 */
export default function DeviceTrendChart({ deviceId, initialStart, initialEnd, className = '' }) {
  /* ===== 네온 팔레트 ===== */
  const C = {
    bg: 'linear-gradient(180deg, rgba(0,10,25,.92), rgba(0,16,36,.98))',
    border: 'rgba(0,255,255,.22)',
    shadow: '0 12px 34px rgba(0,0,0,.55), 0 0 22px rgba(0,240,255,.08)',
    text: '#dff9ff',
    textDim: '#a9f0ff',
    axis: '#baf6ff',
    grid: 'rgba(110,231,255,.22)',
    danger: '#ff8b60',
    // 라인 컬러 (네온)
    lineRed: '#ff6b6b',
    lineBlue: '#22d3ee',
    lineGreen: '#34d399',
  };

  // ===== helpers =====
  function toYmd(d) { const y = d.getFullYear(); const m = `${d.getMonth() + 1}`.padStart(2, '0'); const day = `${d.getDate()}`.padStart(2, '0'); return `${y}-${m}-${day}`; }
  function addDays(d, diff) { const nd = new Date(d); nd.setDate(nd.getDate() + diff); return nd; }
  function round2(v) { return Math.round((v + Number.EPSILON) * 100) / 100; }
  function pad2(n) { return String(n).padStart(2, '0'); }

  const today = useMemo(() => toYmd(new Date()), []);
  const sevenDaysAgo = useMemo(() => toYmd(addDays(new Date(), -6)), []);
  const [start, setStart] = useState(initialStart || sevenDaysAgo);
  const [end, setEnd] = useState(initialEnd || today);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const tempCanvasRef = useRef(null);
  const comboCanvasRef = useRef(null);
  const chartRefs = useRef({ temp: null, combo: null });

  const sameDay = start && end && start === end;
  const disabled = !deviceId || !start || !end || start > end;

  const fetchData = async () => {
    if (disabled) return;
    setLoading(true);
    setErr('');
    try {
      let list = [];
      if (sameDay) {
        // 시간대별 평균 우선
        try {
          const r1 = await api.get(`/${encodeURIComponent(deviceId)}/hourly-avg`, { params: { date: start } });
          list = Array.isArray(r1.data) ? r1.data : [];
          list = list
            .filter((d) => d && (d.hour !== undefined && d.hour !== null))
            .sort((a, b) => Number(a.hour) - Number(b.hour));
        } catch {
          // 폴백: 같은 날짜로 일자별 API
          const r2 = await api.get(`/devices/${encodeURIComponent(deviceId)}/daily-avg`, { params: { start, end } });
          list = Array.isArray(r2.data) ? r2.data : [];
        }
      } else {
        // 기간(여러 일자)
        const r = await api.get(`/devices/${encodeURIComponent(deviceId)}/daily-avg`, { params: { start, end } });
        list = Array.isArray(r.data) ? r.data : [];
      }
      setData(list);
    } catch (e) {
      console.error(e);
      setErr('데이터 조회에 실패했습니다.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [deviceId, start, end]);

  // ===== 네온 Chart 옵션 (공통) =====
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: C.text },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0,16,36,.96)',
        borderWidth: 1,
        borderColor: C.border,
        titleColor: C.text,
        bodyColor: C.text,
        displayColors: true,
      },
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        ticks: { color: C.axis, maxRotation: 0 },
        grid: { color: C.grid },
        title: { display: true, text: '', color: C.textDim },
      },
      y: {
        ticks: { color: C.axis },
        grid: { color: C.grid },
        title: { display: true, text: '', color: C.textDim },
      },
    },
  };

  useEffect(() => {
    const destroyAll = () => {
      if (chartRefs.current.temp) { chartRefs.current.temp.destroy(); chartRefs.current.temp = null; }
      if (chartRefs.current.combo) { chartRefs.current.combo.destroy(); chartRefs.current.combo = null; }
    };
    destroyAll();
    if (!data || data.length === 0) return;

    const labels = sameDay ? data.map((d) => `${pad2(d.hour ?? 0)}:00`) : data.map((d) => d.day);
    const temperature = data.map((d) => round2(d.temperature ?? 0));
    const mss = data.map((d) => round2(d.movingStandardSmoke ?? 0));
    const sav = data.map((d) => round2(d.smokeAvgValue ?? 0));

    // 온도 차트
    if (tempCanvasRef.current) {
      chartRefs.current.temp = new Chart(tempCanvasRef.current.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: '온도',
            data: temperature,
            tension: 0.28,
            pointRadius: 2,
            borderWidth: 2,
            borderColor: C.lineRed,
            backgroundColor: 'rgba(255,107,107,.15)',
            fill: true,
          }],
        },
        options: {
          ...commonOptions,
          scales: {
            ...commonOptions.scales,
            x: { ...commonOptions.scales.x, title: { display: true, text: sameDay ? '시간' : '날짜', color: C.textDim } },
            y: { ...commonOptions.scales.y, title: { display: true, text: '온도', color: C.textDim } },
          },
        },
      });
    }

    // 연기 값 차트
    if (comboCanvasRef.current) {
      chartRefs.current.combo = new Chart(comboCanvasRef.current.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: '연기 농도 기준값',
              data: mss,
              tension: 0.28,
              pointRadius: 2,
              borderWidth: 2,
              borderColor: C.lineBlue,
              backgroundColor: 'rgba(34,211,238,.14)',
              fill: true,
            },
            {
              label: '연기 농도값',
              data: sav,
              tension: 0.28,
              pointRadius: 2,
              borderWidth: 2,
              borderColor: C.lineGreen,
              backgroundColor: 'rgba(52,211,153,.14)',
              fill: true,
            },
          ],
        },
        options: {
          ...commonOptions,
          scales: {
            ...commonOptions.scales,
            x: { ...commonOptions.scales.x, title: { display: true, text: sameDay ? '시간' : '날짜', color: C.textDim } },
            y: { ...commonOptions.scales.y, title: { display: true, text: '연기 값', color: C.textDim } },
          },
        },
      });
    }

    return () => destroyAll();
  }, [data, sameDay]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ===== 스타일 ===== */
  const cardStyle = {
    background: C.bg,
    borderRadius: 14,
    padding: '16px 18px',
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
    color: C.text,
  };
  const headerStyle = {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 12,
  };
  const titleStyle = {
    margin: 0,
    fontSize: 18,
    color: C.text,
    textShadow: '0 0 8px rgba(0,255,255,.28)',
  };
  const subTitleStyle = {
    margin: '0 0 6px 0',
    fontSize: 14,
    color: C.textDim,
  };
  const controlsStyle = { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' };
  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
  const labelStyle = { fontSize: 12, color: C.textDim };
  const inputStyle = {
    height: 34,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    padding: '0 10px',
    background: 'rgba(0,18,36,.65)',
    color: C.text,
    outline: 'none',
  };
  const buttonStyle = {
    height: 34,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    padding: '0 12px',
    background: 'rgba(0,25,55,.65)',
    color: C.text,
    cursor: 'pointer',
    fontWeight: 700,
    transition: 'transform .12s ease, box-shadow .2s ease, background .2s ease, border-color .2s ease',
  };
  const loadingStyle = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim };
  const errorStyle = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.danger, fontWeight: 700 };
  const emptyStyle = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim };
  const footerStyle = { marginTop: 10, color: C.textDim };

  // hover 효과를 인라인로 주기 위해 핸들러 사용
  const onBtnEnter = (e) => {
    e.currentTarget.style.background = 'rgba(0,32,72,.82)';
    e.currentTarget.style.borderColor = 'rgba(0,255,255,.6)';
    e.currentTarget.style.boxShadow = '0 0 12px rgba(0,255,255,.35)';
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const onBtnLeave = (e) => {
    e.currentTarget.style.background = 'rgba(0,25,55,.65)';
    e.currentTarget.style.borderColor = C.border;
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.transform = 'none';
  };
  const onInputFocus = (e) => { e.target.style.boxShadow = '0 0 12px rgba(0,255,255,.28)'; };
  const onInputBlur = (e) => { e.target.style.boxShadow = 'none'; };

  return (
    <div className={`device-trend-card ${className}`} style={cardStyle}>
      <div style={headerStyle}>
        <div>
          <h2 style={titleStyle}>{sameDay ? '시간대 평균' : '일자별 평균'}</h2>
          <div style={{ color: C.textDim, fontSize: 13 }}>
            Device: <strong style={{ color: C.text }}>{deviceId || '-'}</strong>
          </div>
        </div>

        <div style={controlsStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>시작일</label>
            <input type="date" value={start} max={end || undefined}
              onChange={(e) => setStart(e.target.value)} style={inputStyle}
              onFocus={onInputFocus} onBlur={onInputBlur} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>종료일</label>
            <input type="date" value={end} min={start || undefined}
              onChange={(e) => setEnd(e.target.value)} style={inputStyle}
              onFocus={onInputFocus} onBlur={onInputBlur} />
          </div>
          <button onClick={fetchData} disabled={disabled || loading} style={buttonStyle}
            onMouseEnter={onBtnEnter} onMouseLeave={onBtnLeave} title="다시 조회">
            조회
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        <section style={{ height: 220, position: 'relative' }}>
          <h3 style={subTitleStyle}>온도</h3>
          {loading ? (<div style={loadingStyle}>⏳ 불러오는 중…</div>)
          : err ? (<div style={errorStyle}>{err}</div>)
          : data.length === 0 ? (<div style={emptyStyle}>표시할 데이터가 없습니다.</div>)
          : (<canvas ref={tempCanvasRef} />)}
        </section>

        <section style={{ height: 260, position: 'relative' }}>
          <h3 style={subTitleStyle}>연기 값</h3>
          {loading ? (<div style={loadingStyle}>⏳ 불러오는 중…</div>)
          : err ? (<div style={errorStyle}>{err}</div>)
          : data.length === 0 ? (<div style={emptyStyle}>표시할 데이터가 없습니다.</div>)
          : (<canvas ref={comboCanvasRef} />)}
        </section>
      </div>

      <div style={footerStyle}>
        <small>
          평균은 선택한 기간에 따라 {sameDay ? '시간대별' : '일자별'} 데이터 기준으로 계산됩니다. (KST)
        </small>
      </div>
    </div>
  );
}
